// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Modal, Tabs } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import WhatsAppTab from './tabs/WhatsAppTab';
import SlaTab from './tabs/SlaTab';
import CleanupTab from './tabs/CleanupTab';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OmnichannelParametersModal = ({ show, setShow, afterClose, initialTab, resetInitialTab }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const defaultTab = 'whatsapp';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(initialTab || defaultTab);
  const [tabLoading, setTabLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Navigate to initialTab when modal opens with a specific tab requested
  useEffect(() => {
    if (show && initialTab) {
      setActiveTab(initialTab);
    }
  }, [show, initialTab]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const handleAfterClose = useCallback(() => {
    setActiveTab(defaultTab);
    resetInitialTab?.();
    afterClose?.();
  }, [afterClose, resetInitialTab, defaultTab]);

  const handleTabChange = useCallback(key => {
    setActiveTab(key);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      children: <WhatsAppTab loading={tabLoading} setLoading={setTabLoading} />,
    },
    {
      key: 'sla',
      label: 'SLA',
      children: <SlaTab loading={tabLoading} setLoading={setTabLoading} />,
    },
    {
      key: 'cleanup',
      label: translateX('cleaning'),
      children: <CleanupTab loading={tabLoading} setLoading={setTabLoading} />,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX('parameters')}
        </span>
      }
      open={show}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      destroyOnHidden
      width={680}
      footer={null}
      style={{ top: '5vh' }}
      styles={{ body: { overflowY: 'unset' } }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OmnichannelParametersModal;
